/**
 * Kiléptetés tétlenség után.
 *
 * Az időt a szerver adja (`idle_logout` a bejelentkezett felhasználó adataiban, felületenként
 * és környezetenként külön), a tétlenséget viszont a felület méri: a munkamenet lejárata nem
 * elég, mert a háttérben futó hívások (értesítés, verzió) folyamatosan megújítják, így egy
 * nyitva hagyott fül sosem járna le. Tevékenységnek csak a felhasználó keze számít (egér,
 * billentyű, érintés, görgetés) — a háttérhívás nem.
 *
 * A fülek egymás között a `localStorage`-on át egyeztetnek: az egyikben végzett munka a
 * másikat is életben tartja, és ha az egyik kiléptet, a többi is a belépő oldalra megy.
 *
 * A szöveg is a szervertől jön (`idle_logout.wording`), ugyanazért, amiért a jelszó-lejáratnál:
 * a figyelmeztetésnek a fordítás-csomagtól függetlenül olvashatónak kell lennie.
 */
const LAST_ACTIVITY_KEY = 'xmed.idle.lastActivity';
const LOGGED_OUT_KEY = 'xmed.idle.loggedOut';
const MESSAGE_KEY = 'xmed.idle.message';
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'wheel', 'scroll', 'touchstart'];
const TICK_MS = 1000;
const WRITE_THROTTLE_MS = 5000;
function readStorage(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}
function writeStorage(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch (e) {
    // privát ablak vagy tiltott tároló: a fül akkor is a saját óráját méri
  }
}
export default {
  name: 'IdleLogout',
  data() {
    return {
      lastActivity: Date.now(),
      lastWritten: 0,
      remainingMs: null,
      timer: null,
      loggingOut: false
    };
  },
  computed: {
    policy() {
      const user = this.$store.getters['user/authenticated'];
      return this.$store.getters['auth/authenticated'] && user?.idle_logout?.minutes > 0 ? user.idle_logout : null;
    },
    warningVisible() {
      return this.policy !== null && this.remainingMs !== null && this.remainingMs <= this.policy.warning_seconds * 1000;
    },
    secondsLeft() {
      return Math.max(0, Math.ceil((this.remainingMs || 0) / 1000));
    },
    body() {
      return (this.policy?.wording?.body || '').replace(':seconds', this.secondsLeft);
    }
  },
  watch: {
    policy: {
      immediate: true,
      handler(policy) {
        policy ? this.start() : this.stop();
      }
    }
  },
  beforeUnmount() {
    this.stop();
  },
  methods: {
    start() {
      if (this.timer) {
        return;
      }
      this.markActivity(true);
      ACTIVITY_EVENTS.forEach(event => window.addEventListener(event, this.onActivity, {
        passive: true,
        capture: true
      }));
      window.addEventListener('storage', this.onStorage);
      this.timer = window.setInterval(this.tick, TICK_MS);
    },
    stop() {
      ACTIVITY_EVENTS.forEach(event => window.removeEventListener(event, this.onActivity, {
        capture: true
      }));
      window.removeEventListener('storage', this.onStorage);
      window.clearInterval(this.timer);
      this.timer = null;
      this.remainingMs = null;
    },
    onActivity() {
      // A figyelmeztetés alatt a mozdulat nem hosszabbít: ott a gombbal kell dönteni,
      // különben egy véletlen egérmozdulat eltüntetné, mielőtt elolvasná.
      if (!this.warningVisible) {
        this.markActivity();
      }
    },
    markActivity(force = false) {
      const now = Date.now();
      this.lastActivity = now;
      if (force || now - this.lastWritten >= WRITE_THROTTLE_MS) {
        this.lastWritten = now;
        writeStorage(LAST_ACTIVITY_KEY, String(now));
      }
      this.tick();
    },
    onStorage(event) {
      if (event.key === LOGGED_OUT_KEY && event.newValue) {
        this.redirectToLogin();
      }
    },
    tick() {
      if (!this.policy) {
        return;
      }
      const shared = Number(readStorage(LAST_ACTIVITY_KEY)) || 0;
      const last = Math.max(this.lastActivity, shared);
      this.remainingMs = last + this.policy.minutes * 60000 - Date.now();
      if (this.remainingMs <= 0) {
        this.logout();
      }
    },
    stay() {
      this.markActivity(true);
    },
    async logout() {
      if (this.loggingOut) {
        return;
      }
      this.loggingOut = true;
      this.stop();
      try {
        window.sessionStorage.setItem(MESSAGE_KEY, this.policy?.wording?.logged_out || '');
      } catch (e) {
        // üzenet nélkül is kiléptetünk
      }
      writeStorage(LOGGED_OUT_KEY, String(Date.now()));
      try {
        await this.$store.dispatch('auth/logout');
      } catch (e) {
        // a munkamenet már lejárhatott — a belépő oldal ettől még a helyes cél
      }
      this.redirectToLogin();
    },
    redirectToLogin() {
      window.location.href = this.$router.resolve({
        name: 'login'
      }).href;
    }
  }
};